import { useEffect, useState } from 'react';
import { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';

import { DEFAULT_DOOR_LEAD_MS, defaultDoorTime } from 'features/common/utils/dateUtils';

const START_FIELD = 'dates.0.start_date';
const END_FIELD = 'dates.0.end_date';
const DOOR_FIELD = 'door_time';
const ADJUST_FIELD = 'adjust_end_date';

/** The trio bound by the cross-field scheduling rules — always validated together. */
const SCHEDULING_FIELDS = [START_FIELD, END_FIELD, DOOR_FIELD];

/**
 * What the "Adjust Show Dates" toggle promises. Exported so every Edit-Show surface prints the same
 * sentence and the promise cannot drift from what this hook actually does.
 */
export const ADJUST_SHOW_DATES_INFO =
	'Automatically shifts End Date and Open Doors to keep the same relative timing with the new Start Date';

/**
 * The tooltip copy for the scheduling trio and the toggle that moves them. Every Edit-Show surface
 * prints these; they describe what this hook does, so they live beside it.
 */
export const SHOW_SCHEDULE_HELP = {
	adjustShowDates: {
		mainText:
			'On — moving Show Start Date moves Show End Date and Open Doors by the same amount, so the show keeps its length and doors keep their lead. Off — the other dates stay where you put them, and Save checks that doors open before the start and the show ends after it.',
		secondaryText:
			'Example: a 7:00 PM show, doors 6:15 PM, ends 10:00 PM. Move the start to 8:00 PM → doors 7:15 PM, ends 11:00 PM.',
	},
	startDate: 'When the show begins. With Adjust Show Dates on, moving it also moves Show End Date and Open Doors.',
	endDate:
		'When the show ends — must be after Show Start Date. With Adjust Show Dates on it moves with the start; off, it stays where you put it.',
	openDoors:
		'When guests can start coming in — must be before Show Start Date. With Adjust Show Dates on it keeps its lead when the start moves; off, it stays where you put it.',
} as const;

/** The show's persisted schedule, as the API serves it (ISO strings, not form values). */
export interface PersistedShowSchedule {
	startDate?: string | null;
	endDate?: string | null;
	doorTime?: string | null;
}

/** A show payload, narrowed to the fields its schedule is read from. */
export interface ShowScheduleSource {
	days: { start_date: string; end_date: string }[];
	door_time: string | null;
}

/** The one mapping from a show payload to its persisted schedule, shared by all three Edit-Show surfaces. */
export const persistedScheduleOf = (show: ShowScheduleSource): PersistedShowSchedule => ({
	startDate: show.days[0]?.start_date,
	endDate: show.days[0]?.end_date,
	doorTime: show.door_time,
});

export interface ShowScheduleSync {
	/** Wire it as the Show Start Date picker's onChange: travels the timeline, then re-checks the trio. */
	handleStartDateChange: () => void;
	/** Wire it as the End Date and Open Doors pickers' onChange. */
	revalidateScheduling: () => void;
}

const durationMs = (from?: string | null, to?: string | null): number | null => {
	if (!from || !to) return null;
	const fromMs = new Date(from).getTime();
	const toMs = new Date(to).getTime();
	if (isNaN(fromMs) || isNaN(toMs)) return null;
	return toMs - fromMs;
};

const isValidDate = (value: unknown): value is Date => value instanceof Date && !isNaN(value.getTime());

/**
 * Keeps a show's three scheduling fields consistent while the manager edits it — the single definition
 * shared by the classic Edit Show form, the green Edit Show sub-page, and the show-dashboard General
 * section.
 *
 * With "Adjust Show Dates" on, the whole timeline travels with the start: the show keeps its length and
 * the doors keep their lead, so moving the start alone never leaves End or Open Doors behind. With the
 * toggle off only an end that fell behind the start is rescued — an inconsistent Open Doors is reported
 * by the schema rather than silently moved, because off means the manager is placing the fields.
 */
export const useShowScheduleSync = <T extends FieldValues>(
	formMethods: UseFormReturn<T>,
	persisted: PersistedShowSchedule
): ShowScheduleSync => {
	// The paths are identical on every surface, but T is only known at the call site — cast once here
	// instead of at each watch/getValues/setValue.
	const startField = START_FIELD as Path<T>;
	const endField = END_FIELD as Path<T>;
	const doorField = DOOR_FIELD as Path<T>;
	const adjustField = ADJUST_FIELD as Path<T>;

	// Both spans describe the shape of the timeline, so they are read once from the persisted show:
	// moving the start travels the timeline, it does not redefine it.
	const [originalTimeDiff] = useState(() => durationMs(persisted.startDate, persisted.endDate) ?? 0);
	const [originalDoorLead] = useState(() => {
		const lead = durationMs(persisted.doorTime, persisted.startDate);
		// A show with no stored door time — or one stored AFTER its start, the invalid state this sync
		// exists to stop — takes the standard lead rather than carrying a negative one forward.
		return lead === null || lead < 0 ? DEFAULT_DOOR_LEAD_MS : lead;
	});

	const startDate = formMethods.watch(startField);
	const adjustEndDate = formMethods.watch(adjustField);

	const revalidateScheduling = () => {
		void formMethods.trigger(SCHEDULING_FIELDS as Path<T>[]);
	};

	/** Writes only a real move, so running the sync over an already-synced form costs no render. */
	const writeDate = (field: Path<T>, next: Date): boolean => {
		const current = formMethods.getValues(field) as unknown;
		if (isValidDate(current) && current.getTime() === next.getTime()) return false;
		formMethods.setValue(field, next as PathValue<T, Path<T>>);
		return true;
	};

	/** Brings End Date and Open Doors into line with the current start. Reports whether anything moved. */
	const syncToStart = (): boolean => {
		const start = formMethods.getValues(startField) as unknown;
		if (!isValidDate(start)) return false;

		// End and Open Doors are read, never watched: this sync writes them, and each write installs a
		// fresh Date instance — watching one would re-fire the effect below forever.
		const door = formMethods.getValues(doorField) as unknown;
		const travelWithStart = Boolean(formMethods.getValues(adjustField)) && originalTimeDiff > 0;
		let moved = false;

		if (!door && !travelWithStart) {
			const seeded = defaultDoorTime(start);
			if (seeded) moved = writeDate(doorField, seeded) || moved;
		}

		if (travelWithStart) {
			moved = writeDate(endField, new Date(start.getTime() + originalTimeDiff)) || moved;
			moved = writeDate(doorField, new Date(start.getTime() - originalDoorLead)) || moved;
		} else {
			const end = formMethods.getValues(endField) as unknown;
			if (isValidDate(end) && end < start) moved = writeDate(endField, start) || moved;
		}

		return moved;
	};

	const handleStartDateChange = () => {
		syncToStart();
		revalidateScheduling();
	};

	useEffect(() => {
		/* why this effect exists, and revalidates only over a real error: CLAUDE.md → Why it is shaped this way */
		if (syncToStart() && Object.keys(formMethods.formState.errors).length > 0) revalidateScheduling();
	}, [startDate, adjustEndDate, originalTimeDiff, originalDoorLead]);

	return { handleStartDateChange, revalidateScheduling };
};
